/**
 * 场景评论 Drawer（共享组件）。
 *
 * 3 个入口共用：
 * - 任务列表入口：传 ``jobId`` + ``taskId`` 创建任务维度评论；列表也按 ``(scene, job)``
 *   过滤；支持按内容关键字搜索。
 * - 场景管理 / 全量场景列表入口：不传 ``jobId/taskId``；列表按场景拉历史全部
 *   评论（含任务维度 + 场景维度）；**只读**，不可新建 / 编辑 / 删除。
 *
 * 视觉：refined editorial。
 * - 顶 composer 卡片：textarea 内置 maxLength 字符计数（antd 默认在右下浮起，
 *   不与下方"发布"按钮重叠）+ 一行右对齐 primary 按钮。
 * - 中段 toolbar：搜索独立，与列表分割。
 * - 列表卡片：左侧色条标识维度（任务维度蓝 / 场景维度灰），头像首字母圆点，
 *   时间靠右灰小字，hover 微浮起。
 *
 * 行为：仅作者可编辑/删除；删走物理删；编辑走 inline。
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Drawer,
  Input,
  Button,
  Empty,
  Spin,
  message,
  Popconfirm,
  Space,
  Tag,
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  SaveOutlined,
  CloseOutlined,
  SearchOutlined,
  CommentOutlined,
  SendOutlined,
} from '@ant-design/icons';

import {
  createSceneComment,
  deleteSceneComment,
  listSceneComments,
  updateSceneComment,
  type SceneCommentVo,
} from '@/apis/simulation';
import { useCurrentUser } from '@/common/auth/adminAcl';

interface Props {
  open: boolean;
  onClose: () => void;
  sceneId: number;
  sceneName?: string | null;
  /** 任务列表入口才传；带值则按 (scene, job) 过滤 + 创建时落任务维度 */
  jobId?: number;
  /** 与 jobId 配对，任务列表入口必填 */
  taskId?: number;
  /** 标题前缀，便于区分入口（"任务列表 - 评论" / "场景评论"） */
  title?: string;
}

const MAX_CONTENT_LEN = 2000;

// ── 设计 token：dominant grey + sharp accent，cohesive 灰阶 + 两个语义色
const T = {
  border: '#ebebeb',
  borderHover: '#d9d9d9',
  bg: '#fafafa',
  bgHover: '#f5f5f7',
  textPrimary: '#1d1d1f',
  textSecondary: '#6b6f76',
  textMuted: '#9b9da3',
  accentJob: '#5e6ad2',     // 任务维度
  accentScene: '#9b9da3',   // 场景维度
};

/** 头像圆点：取作者首字母 + 哈希色（同人同色，跨人区分）。 */
const AVATAR_PALETTE = ['#5e6ad2', '#7c3aed', '#0284c7', '#16a34a', '#d97706', '#dc2626', '#0891b2', '#ea580c'];

function avatarFor(label: string) {
  const text = label?.trim() || '?';
  const ch = text[0] || '?';
  let hash = 0;
  for (let i = 0; i < text.length; i++) hash = (hash * 31 + text.charCodeAt(i)) >>> 0;
  return { ch: ch.toUpperCase(), color: AVATAR_PALETTE[hash % AVATAR_PALETTE.length] };
}

function formatLocal(s: string | null | undefined): string {
  if (!s) return '-';
  // 后端返回如 "2026-06-25T17:41:02.437318"；不依赖 dayjs，取 [0:19] 替换 T 为空格
  return s.replace('T', ' ').slice(0, 19);
}

const SceneCommentDrawer: React.FC<Props> = ({
  open,
  onClose,
  sceneId,
  sceneName,
  jobId,
  taskId,
  title,
}) => {
  const { me } = useCurrentUser();
  const myEmail = (me?.user?.email || '').toLowerCase();

  const [items, setItems] = useState<SceneCommentVo[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [draft, setDraft] = useState('');
  const [draftTitle, setDraftTitle] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingDraft, setEditingDraft] = useState('');
  const [editingTitle, setEditingTitle] = useState('');

  const isJobMode = jobId != null && taskId != null;
  /** 任务列表入口可写；场景管理 / 全量场景入口只读。 */
  const canWrite = isJobMode;

  const reload = useCallback(async (kw?: string) => {
    if (!sceneId || !open) return;
    setLoading(true);
    try {
      const data = await listSceneComments({
        sceneId,
        jobId: isJobMode ? jobId : undefined,
        keyword: (kw ?? keyword).trim() || undefined,
        current: 1,
        pageSize: 200,
      });
      setItems(data.items);
      setTotal(data.total);
    } catch (e) {
      console.error('加载评论失败', e);
      message.error('加载评论失败');
    } finally {
      setLoading(false);
    }
  }, [sceneId, open, isJobMode, jobId, keyword]);

  useEffect(() => {
    if (open) {
      reload();
    } else {
      setItems([]);
      setKeyword('');
      setDraft('');
      setDraftTitle('');
      setEditingId(null);
      setEditingTitle('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sceneId, jobId, taskId]);

  const handleSubmit = async () => {
    const text = draft.trim();
    if (!text) {
      message.warning('请输入评论内容');
      return;
    }
    setSubmitting(true);
    try {
      await createSceneComment({
        sceneId,
        jobId: isJobMode ? jobId : null,
        taskId: isJobMode ? taskId : null,
        title: draftTitle.trim() || null,
        content: text,
      });
      setDraft('');
      setDraftTitle('');
      message.success('评论已发布');
      await reload();
    } catch (e) {
      console.error('发布评论失败', e);
      message.error('发布评论失败');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStartEdit = (item: SceneCommentVo) => {
    setEditingId(item.id);
    setEditingDraft(item.content);
    setEditingTitle(item.title || '');
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingDraft('');
    setEditingTitle('');
  };

  const handleSaveEdit = async () => {
    if (editingId == null) return;
    const text = editingDraft.trim();
    if (!text) {
      message.warning('内容不能为空');
      return;
    }
    try {
      // editingTitle 空 → 传 ""，让后端按新 content 重新派生（与未传旧值 null 区分）
      await updateSceneComment(editingId, text, editingTitle.trim());
      message.success('已更新');
      setEditingId(null);
      setEditingDraft('');
      setEditingTitle('');
      await reload();
    } catch (e) {
      console.error('更新评论失败', e);
      message.error('更新评论失败');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteSceneComment(id);
      message.success('已删除');
      await reload();
    } catch (e) {
      console.error('删除评论失败', e);
      message.error('删除评论失败');
    }
  };

  const drawerTitle = useMemo(() => {
    const head = title || (isJobMode ? '任务评论' : '场景评论');
    const detail = sceneName
      ? `${head} · ${sceneName}`
      : `${head} · 场景 #${sceneId}`;
    return (
      <Space size={8}>
        <CommentOutlined style={{ color: T.accentJob }} />
        <span style={{ fontWeight: 600, color: T.textPrimary }}>{detail}</span>
        <Tag style={{ marginLeft: 4, background: T.bg, border: `1px solid ${T.border}`, color: T.textSecondary }}>
          {total}
        </Tag>
        {!canWrite ? (
          <Tag style={{ background: T.bg, border: `1px solid ${T.border}`, color: T.textMuted }}>
            只读
          </Tag>
        ) : null}
      </Space>
    );
  }, [title, isJobMode, sceneName, sceneId, total, canWrite]);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={drawerTitle}
      width={560}
      destroyOnHidden
      styles={{ body: { padding: 16, background: '#fff' } }}
    >
      {/* ── 顶部 composer：仅任务列表入口可发布 ── */}
      {canWrite ? (
      <div
        style={{
          background: '#fff',
          border: `1px solid ${T.border}`,
          borderRadius: 8,
          padding: 12,
          marginBottom: 16,
          boxShadow: '0 1px 2px rgba(0,0,0,0.02)',
        }}
      >
        {/* 标题（可选）：列表展示用；不填后端取 content 前 6 字符 */}
        <Input
          value={draftTitle}
          onChange={(e) => setDraftTitle(e.target.value)}
          placeholder="标题（可选，≤10 字；不填取正文前 6 字）"
          maxLength={10}
          variant="borderless"
          style={{ padding: 0, fontSize: 13, fontWeight: 500, color: T.textPrimary }}
        />
        <div style={{ height: 1, background: T.border, margin: '8px 0' }} />
        <Input.TextArea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={
            isJobMode
              ? `评论场景在该任务（job #${jobId}）中的表现…`
              : '为该场景留下评论…'
          }
          autoSize={{ minRows: 3, maxRows: 8 }}
          maxLength={MAX_CONTENT_LEN}
          variant="borderless"
          style={{ padding: 0, fontSize: 13, lineHeight: 1.6, resize: 'none' }}
        />
        {/* 计数（左）+ 发布按钮（右）独立一行，物理隔离不重叠 */}
        <div
          style={{
            marginTop: 10,
            paddingTop: 10,
            borderTop: `1px dashed ${T.border}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <span style={{ fontSize: 12, color: T.textMuted, fontVariantNumeric: 'tabular-nums' }}>
            {draft.length} / {MAX_CONTENT_LEN}
          </span>
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={handleSubmit}
            loading={submitting}
            disabled={!draft.trim()}
          >
            发布
          </Button>
        </div>
      </div>
      ) : null}

      {/* ── 搜索 toolbar ── */}
      <Input
        prefix={<SearchOutlined style={{ color: T.textMuted }} />}
        placeholder="按内容搜索…"
        value={keyword}
        onChange={(e) => setKeyword(e.target.value)}
        onPressEnter={() => reload()}
        allowClear
        style={{ marginBottom: 16 }}
      />

      {/* ── 列表 ── */}
      <Spin spinning={loading}>
        {items.length === 0 ? (
          <div style={{ padding: '32px 0' }}>
            <Empty
              description={
                <span style={{ color: T.textMuted, fontSize: 13 }}>
                  {loading ? null : keyword
                    ? '没有匹配的评论'
                    : (canWrite ? '暂无评论，写下第一条吧' : '暂无评论')}
                </span>
              }
            />
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {items.map((item) => {
              const isMine = (item.creator || '').toLowerCase() === myEmail;
              const isJobItem = item.jobId != null;
              const accent = isJobItem ? T.accentJob : T.accentScene;
              const av = avatarFor(item.creatorName || item.creator);
              const editing = editingId === item.id;
              return (
                <div
                  key={item.id}
                  style={{
                    position: 'relative',
                    padding: '12px 14px 12px 18px',
                    background: '#fff',
                    border: `1px solid ${T.border}`,
                    borderRadius: 8,
                    transition: 'border-color 0.15s, box-shadow 0.15s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = T.borderHover;
                    e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.04)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = T.border;
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  {/* 左侧色条：标识维度 */}
                  <div
                    style={{
                      position: 'absolute',
                      left: 0,
                      top: 12,
                      bottom: 12,
                      width: 3,
                      background: accent,
                      borderRadius: '0 2px 2px 0',
                      opacity: 0.85,
                    }}
                  />
                  {/* 头部：头像 + 作者 + 维度 tag + 时间 */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                    <div
                      style={{
                        width: 26, height: 26, borderRadius: '50%',
                        background: av.color, color: '#fff',
                        fontSize: 12, fontWeight: 600,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      {av.ch}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, minWidth: 0 }}>
                        <span style={{
                          fontSize: 13, fontWeight: 600, color: T.textPrimary,
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        }}>
                          {item.creatorName || item.creator}
                        </span>
                        {item.creatorName ? (
                          <span style={{
                            fontSize: 11, color: T.textMuted,
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                          }}>
                            {item.creator}
                          </span>
                        ) : null}
                      </div>
                      <div style={{ fontSize: 11, color: T.textMuted, marginTop: 1 }}>
                        {formatLocal(item.createTime)}
                        {item.updateTime ? (
                          <span style={{ marginLeft: 6 }}>
                            · 编辑于 {formatLocal(item.updateTime)}
                          </span>
                        ) : null}
                      </div>
                    </div>
                    {/* 维度 tag：极简 pill，颜色与左侧色条对齐 */}
                    <span
                      style={{
                        fontSize: 11,
                        padding: '1px 8px',
                        borderRadius: 10,
                        background: isJobItem ? '#eef0fb' : T.bg,
                        color: accent,
                        border: `1px solid ${isJobItem ? '#dde0f3' : T.border}`,
                        flexShrink: 0,
                        whiteSpace: 'nowrap',
                      }}
                      title={isJobItem ? `任务维度 · taskId=${item.taskId}` : '场景维度'}
                    >
                      {isJobItem ? `Job #${item.jobId}` : '场景'}
                    </span>
                  </div>

                  {/* 内容（编辑态切换） */}
                  {editing ? (
                    <div
                      style={{
                        background: T.bg,
                        border: `1px solid ${T.border}`,
                        borderRadius: 6,
                        padding: 10,
                      }}
                    >
                      <Input
                        value={editingTitle}
                        onChange={(e) => setEditingTitle(e.target.value)}
                        placeholder="标题（可选，≤10 字；留空按新正文前 6 字派生）"
                        maxLength={10}
                        variant="borderless"
                        style={{ padding: 0, fontSize: 13, fontWeight: 500, color: T.textPrimary }}
                      />
                      <div style={{ height: 1, background: T.border, margin: '6px 0' }} />
                      <Input.TextArea
                        value={editingDraft}
                        onChange={(e) => setEditingDraft(e.target.value)}
                        autoSize={{ minRows: 3, maxRows: 6 }}
                        maxLength={MAX_CONTENT_LEN}
                        variant="borderless"
                        style={{ padding: 0, fontSize: 13, lineHeight: 1.6, resize: 'none' }}
                      />
                      <div
                        style={{
                          marginTop: 8,
                          paddingTop: 8,
                          borderTop: `1px dashed ${T.border}`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 12,
                        }}
                      >
                        <span style={{ fontSize: 12, color: T.textMuted, fontVariantNumeric: 'tabular-nums' }}>
                          {editingDraft.length} / {MAX_CONTENT_LEN}
                        </span>
                        <Space size={6}>
                          <Button size="small" icon={<CloseOutlined />} onClick={handleCancelEdit}>
                            取消
                          </Button>
                          <Button
                            size="small"
                            type="primary"
                            icon={<SaveOutlined />}
                            onClick={handleSaveEdit}
                          >
                            保存
                          </Button>
                        </Space>
                      </div>
                    </div>
                  ) : (
                    <>
                      {item.title ? (
                        <div style={{
                          fontSize: 13, fontWeight: 600, color: T.textPrimary,
                          marginBottom: 4,
                        }}>
                          {item.title}
                        </div>
                      ) : null}
                      <div
                        style={{
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          fontSize: 13,
                          lineHeight: 1.65,
                          color: '#374151',
                        }}
                      >
                        {item.content}
                      </div>
                    </>
                  )}

                  {/* 仅任务列表入口 + 作者本人可编辑/删除 */}
                  {isMine && canWrite && !editing ? (
                    <div style={{ marginTop: 8, textAlign: 'right' }}>
                      <Space size={4}>
                        <Button
                          size="small"
                          type="text"
                          icon={<EditOutlined />}
                          onClick={() => handleStartEdit(item)}
                          style={{ color: T.textSecondary, fontSize: 12 }}
                        >
                          编辑
                        </Button>
                        <Popconfirm
                          title="确认删除该评论？"
                          okText="删除"
                          cancelText="取消"
                          getPopupContainer={(trigger) => trigger.parentElement ?? document.body}
                          onConfirm={() => handleDelete(item.id)}
                        >
                          <Button
                            size="small"
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            style={{ fontSize: 12 }}
                          >
                            删除
                          </Button>
                        </Popconfirm>
                      </Space>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </Spin>
    </Drawer>
  );
};

export default SceneCommentDrawer;
export { useSceneCommentSummary } from './useSceneCommentSummary';
